import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SelectButton, Button, Icon, Modal, Tooltip } from '@weezevent/nacre';
import RatesManager, { generateClassNameRateName, SortRatesButton } from '../../../rates/components/rateManager';
import LoadingCard from '../../../../components/loadingCard';
import RulesEmpty from '../../list/components/design-row/rulesEmpty';

import { InheritedProperty } from '../../../rates/ratesList';
import { RateCategorySeat } from '../../../rates/components/rateCategorySeat';
import { RateBundleInfo } from '../../../rates/components/rateBundleInfo';
import StyleRM from '../../../rates/components/rateManager.module.css';

import RateModel from '../../../../models/rateModel';
import DesignModel from '../../../../models/designModel';

import Style from '../designForm.module.css';

export default function RatesPicker({ organization = null, designId = null, event = null, prefix, linkedRates = [], onChangeRules = () => {} }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [show, setShow] = React.useState(false);
    const [selectedRatesAdd, setSelectedRatesAdd] = React.useState([]);
    const [selectedRatesDelete, setSelectedRatesDelete] = React.useState([]);
    const [eventRules, setEventRules] = React.useState([]);

    const [, loadingEventRules] = DesignModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: event.id,
            launch: Boolean(event.id && organization.id),
            onSuccess: _designs => {
                const allEventRules = _designs.reduce((allRules, design) => {
                    const designObject = design.object;
                    //for all other designs of the event we get all the rate_id linked by rules in an array
                    if (!designId || designObject.id.toString() !== designId) allRules[designObject.id] = designObject.rules.map(rule => rule.rate_id);
                    return allRules;
                }, {});
                setEventRules(allEventRules);
            }
        },
        [event, organization, designId, setEventRules]
    );

    //we update the selected value to prevent keeping deleted rates selected
    const onUpdateRates = React.useCallback(
        newValue => {
            onChangeRules(newValue);
            setSelectedRatesAdd([]);
            setSelectedRatesDelete([]);
            setShow(false);
        },
        [onChangeRules, setSelectedRatesAdd, setSelectedRatesDelete]
    );

    const onDeleteRates = React.useCallback(
        toDelete => {
            const toDeleteObjects = toDelete.map(rate => (rate.object ? rate.object.id : rate));
            const newRatesValue = linkedRates.filter(rateId => !toDeleteObjects.includes(rateId));
            onUpdateRates(newRatesValue);
        },
        [linkedRates, onChangeRules, onUpdateRates]
    );

    //we get all rates to have the id of all the non-selected rates (right below)
    //The advantage here of using relays is that we'll be able to manage page loading better
    const [allRates, loadingRates] = RateModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: event.id,
            launch: Boolean(organization.id && event.id),
            query: {
                limit: 1000,
                expand: 'relays'
            }
        },
        [organization, event]
    );

    //we filter all rates id on the selected ones we have to get the non-selected list
    const notLinkedRates = React.useMemo(() => {
        if (!allRates) return [];
        const notLinked = allRates.filter(r => !linkedRates.includes(r.id) && r.id);
        return notLinked.map(rate => rate.object.id);
    }, [allRates, linkedRates]);

    //custom rates cell with cross icon (for delete)
    let getTableCells = React.useCallback(
        (hasDelete = false) => {
            return [
                {
                    header: t('ticket.rates.rateName'),
                    renderRate: (rate, { categories, groups }) => {
                        //we get the id of the design that has this rate_id already in its rules
                        const linkedToDesign = Object.keys(eventRules).find(
                            designId => eventRules[designId] && eventRules[designId].length && eventRules[designId].includes(rate.id)
                        );
                        return (
                            <div className={Style['cell-group']}>
                                <InheritedProperty
                                    displayIcon={false}
                                    property="name"
                                    object={rate}
                                    handleProperty={name => (
                                        <div className={StyleRM[generateClassNameRateName(rate, groups)]} style={{ display: 'flex', alignItems: 'center' }}>
                                            <RateCategorySeat event={event} rate={rate} categories={categories} />
                                            {name}
                                            <RateBundleInfo bundleSize={rate.bundle_size || 1} />
                                            {linkedToDesign && (
                                                <Tooltip triggerOnHover title={t('ticket.ticket_design.create_design.already_linked')}>
                                                    <Icon name="link" onClick={() => history.push(`${prefix}${linkedToDesign}`)} />
                                                </Tooltip>
                                            )}
                                        </div>
                                    )}
                                    template="rate_template"
                                />
                                {hasDelete && <Icon name="cross" onClick={() => onDeleteRates([rate.id])} className={Style['delete-icon']} />}
                            </div>
                        );
                    },
                    renderGroup: (group, onToggle) => {
                        return (
                            <div className={StyleRM['rates-table__cell-group']} onClick={() => onToggle(group)}>
                                {group.parent && <Icon className={StyleRM['rates-table__cell-group-icon-subgroup']} name="link-group" />}
                                <Icon name={group.showRates ? 'folder-open' : 'folder-closed'} style={{ marginRight: '5px' }} />
                                <InheritedProperty displayIcon={false} property="name" object={group} handleProperty={name => <b>{name}</b>} template="group_template" />
                            </div>
                        );
                    }
                }
            ];
        },
        [event, eventRules, onDeleteRates]
    );

    return (
        <LoadingCard loading={loadingRates || (event.id && organization.id && loadingEventRules)}>
            <Modal
                size="large"
                open={show}
                onClose={() => setShow(false)}
                title={t('ticket.ticket_design.create_design.tickets')}
                subtitle={t('ticket.ticket_design.create_design.tickets_description')}
                cancelButton={<Button onClick={() => setShow(false)} label={t('ticket.common.action.cancel')} />}
                submitButton={<Button primary onClick={() => onUpdateRates(linkedRates.concat(selectedRatesAdd))} label={t('ticket.common.action.add')} />}
            >
                <RatesManager
                    className={Style['rules-rates-list']}
                    event={event}
                    organization={organization}
                    initialQuery={{
                        organizationId: organization.id,
                        eventId: event.id
                    }}
                    excludeRatesIds={linkedRates}
                    tableCells={getTableCells()}
                    selected={selectedRatesAdd}
                    onRatesSelected={setSelectedRatesAdd}
                    mode={'other'}
                    hideEmptyGroup={false}
                    selectable={true}
                    activeSyncRates={false}
                    activeCacheRates
                    activeCacheGroups
                />
            </Modal>
            {linkedRates.length > 0 ? (
                <RatesManager
                    className={Style['rules-rates-list']}
                    event={event}
                    organization={organization}
                    initialQuery={{
                        organizationId: organization.id,
                        eventId: event.id
                    }}
                    excludeRatesIds={notLinkedRates}
                    tableCells={getTableCells(true)}
                    mode={'other'}
                    hideEmptyGroup={true}
                    selectable={true}
                    activeSyncRates={false}
                    activeCacheRates
                    activeCacheGroups
                    selected={selectedRatesDelete}
                    onRatesSelected={setSelectedRatesDelete}
                >
                    <div className={'container-global-actions'}>
                        <SelectButton
                            tooltipTitle={t('ticket.rates.noActionTooltip')}
                            title={selectedRatesDelete.length > 0 ? t('ticket.rates.rateAction', { count: selectedRatesDelete.length }) : t('ticket.common.action.bulk')}
                            objects={selectedRatesDelete}
                        >
                            <Button onClick={() => onDeleteRates(selectedRatesDelete)}>{t('ticket.cross-selling.listVariant.deleteAll')}</Button>
                        </SelectButton>
                        <div className={'container-secondary-actions'}>
                            <SortRatesButton disabled={false} onClick={() => setShow(true)} label={t('ticket.creationEvent.recap.addRates')} icon="plus" />
                        </div>
                    </div>
                </RatesManager>
            ) : (
                <RulesEmpty event={event} labelButton={t('ticket.creationEvent.recap.addRates')} onClick={() => setShow(true)} />
            )}
        </LoadingCard>
    );
}
